import Page from "../../common/page/Page"
import Loader from "../../common/loader/Loader"
import { useAuth } from "../../data/useAuth"
import style from "./AddressPage.module.css"
import LocationIcon from "../../icons/LocationIcon"
import { useEffect, useState } from "react"
import axios from "axios"
import { Link } from "react-router-dom"
import DeleteIcon from "../../icons/DeleteIcon"
import EditIcon from "../../icons/EditIocn"
import { decodeHtml, decodeAddress } from "../../common/utils/address.util"

const AddressPage = () => {
  const { user } = useAuth()
  const [address, setAddress] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formActive, setFormActive] = useState(false)
  const [isAdd, setIsAdd] = useState(true)
  const [activeState, setActiveState] = useState([])

  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    line_1: "",
    line_2: "",
    city: "",
    district: "",
    state: "",
    country: "",
    pin_code: "",
  })

  const [formError, setFormError] = useState("")

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setFormData((prev) => ({ ...prev, [id]: value }))
    if (formError) setFormError("")
  }

  const isValidState = (name) => {
    return activeState.some(
      (st) => decodeHtml(st.name) === decodeHtml(name) || st.name === name
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError("")

    if (!formData.name.trim()) {
      setFormError("Name is required.")
      return
    }
    if (!formData.phone_number.trim()) {
      setFormError("Phone number is required.")
      return
    }
    if (!/^\d{10}$/.test(formData.phone_number)) {
      setFormError("Phone number must be a valid 10-digit number.")
      return
    }
    if (!formData.line_1.trim()) {
      setFormError("Address Line 1 is required.")
      return
    }
    if (!formData.line_2.trim()) {
      setFormError("Address Line 2 is required.")
      return
    }
    if (!formData.city.trim()) {
      setFormError("City is required.")
      return
    }
    if (!formData.district.trim()) {
      setFormError("District is required.")
      return
    }
    if (!formData.state || !isValidState(formData.state)) {
      setFormError("Please select a valid state.")
      return
    }
    if (!formData.country.trim()) {
      setFormError("Country is required.")
      return
    }
    if (formData.pin_code === "" || isNaN(formData.pin_code)) {
      setFormError("Pin code is required and must be a number.")
      return
    }

    try {
      if (isAdd) {
        const response = await axios.post("/api/address", formData)
        setAddress((prev) => [...prev, decodeAddress(response.data)])
      } else {
        const response = await axios.put(
          `/api/address/${formData.id}`,
          formData
        )
        setAddress((prev) =>
          prev.map((addr) => (addr.id === formData.id ? decodeAddress(response.data) : addr))
        )
      }

      setFormActive(false)
      setFormData({
        name: "",
        phone_number: "",
        line_1: "",
        line_2: "",
        city: "",
        district: "",
        state: "",
        country: "",
        pin_code: "",
      })
      setIsAdd(true)
    } catch (err) {
      setFormError(err.response?.data?.error || err.message)
    }
  }

  const handleEdit = (addressData) => {
    setFormData(decodeAddress(addressData))
    setFormActive(true)
    setIsAdd(false)
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this address?")) return

    try {
      await axios.delete(`/api/address/${id}`)
      setAddress((prev) => prev.filter((addr) => addr.id !== id))
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete address")
    }
  }

  useEffect(() => {
    const fetchAddress = async () => {
      setLoading(true)
      try {
        const [addressRes, statesRes] = await Promise.all([
          axios.get("/api/address/user"),
          axios.get("/api/delivery-states/active"),
        ])

        setAddress((addressRes.data || []).map(decodeAddress))
        setActiveState(statesRes.data || [])
      } catch (err) {
        setError(err.response?.data?.message || err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchAddress()
  }, [])

  return (
    <Page title={"Address"} searchDisabled={true}>
      <div className={style.container}>
        {/* Header Hero Card */}
        <div className={style.headerCard}>
          <div className={style.headerText}>
            <h2>Saved Addresses</h2>
            <p>Manage your delivery and billing locations for quick checkout</p>
          </div>
          {!formActive && (
            <button
              type="button"
              className={style.addBtn}
              onClick={() => {
                setFormActive(true)
                setIsAdd(true)
                setFormData({
                  name: "",
                  phone_number: "",
                  line_1: "",
                  line_2: "",
                  city: "",
                  district: "",
                  state: "",
                  country: "",
                  pin_code: "",
                })
              }}
            >
              <LocationIcon /> Add Address
            </button>
          )}
        </div>

        {!formActive && (
          <>
            {loading ? (
              <div className={style.loaderContainer}>
                <Loader />
              </div>
            ) : error ? (
              <div className={style.errorCard}>{error}</div>
            ) : address.length === 0 ? (
              <div className={style.emptyAddressCard}>
                <div className={style.emptyIconWrapper}>
                  <LocationIcon />
                </div>
                <h3>No Saved Addresses</h3>
                <p>You haven't added any delivery addresses yet.</p>
                <button
                  type="button"
                  className={style.primaryAddBtn}
                  onClick={() => {
                    setFormActive(true)
                    setIsAdd(true)
                  }}
                >
                  + Add New Address
                </button>
              </div>
            ) : (
              <div className={style.grid}>
                {address.map((item) => (
                  <div key={item.id} className={style.addressCard}>
                    <div className={style.addressCardBody}>
                      <div className={style.recipientHeader}>
                        <h4 className={style.recipientName}>{decodeHtml(item.name)}</h4>
                      </div>
                      <p className={style.addressText}>
                        {[decodeHtml(item.line_1), decodeHtml(item.line_2)].filter(Boolean).join(", ")}
                      </p>
                      <p className={style.addressText}>
                        {decodeHtml(item.city)}, {decodeHtml(item.district)}, {decodeHtml(item.state)} - {item.pin_code}
                      </p>
                      <p className={style.addressText}>{decodeHtml(item.country)}</p>
                      <div className={style.phoneChip}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        {item.phone_number}
                      </div>
                    </div>

                    <div className={style.cardActions}>
                      <button
                        type="button"
                        className={`${style.actionBtn} ${style.editBtn}`}
                        onClick={() => handleEdit(item)}
                      >
                        <EditIcon /> Edit
                      </button>
                      <button
                        type="button"
                        className={`${style.actionBtn} ${style.deleteBtn}`}
                        onClick={() => handleDelete(item.id)}
                      >
                        <DeleteIcon /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Address Add / Edit Form Card */}
        {formActive && (
          <div className={style.formCard}>
            <div className={style.formHeader}>
              <div>
                <h3>{isAdd ? "Add New Address" : "Edit Address"}</h3>
                <p>Please complete your address details for accurate delivery</p>
              </div>
              <button
                type="button"
                className={style.backLinkBtn}
                onClick={() => {
                  setFormActive(false)
                  setFormError("")
                }}
              >
                ← Back to addresses
              </button>
            </div>

            <form onSubmit={handleSubmit} className={style.addressForm}>
              <div className={style.formGrid}>
                <div className={style.fieldGroup}>
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    required
                  />
                </div>

                <div className={style.fieldGroup}>
                  <label htmlFor="phone_number">Phone Number *</label>
                  <input
                    type="text"
                    id="phone_number"
                    value={formData.phone_number}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>

                <div className={`${style.fieldGroup} ${style.fullWidth}`}>
                  <label htmlFor="line_1">House / Flat / Office *</label>
                  <input
                    type="text"
                    id="line_1"
                    value={formData.line_1}
                    onChange={handleInputChange}
                    placeholder="Flat / House No. / Building Name"
                    required
                  />
                </div>

                <div className={`${style.fieldGroup} ${style.fullWidth}`}>
                  <label htmlFor="line_2">Street / Area / Landmark *</label>
                  <input
                    type="text"
                    id="line_2"
                    value={formData.line_2}
                    onChange={handleInputChange}
                    placeholder="Street, Landmark or Colony"
                    required
                  />
                </div>

                <div className={style.fieldGroup}>
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="City"
                    required
                  />
                </div>

                <div className={style.fieldGroup}>
                  <label htmlFor="district">District *</label>
                  <input
                    type="text"
                    id="district"
                    value={formData.district}
                    onChange={handleInputChange}
                    placeholder="District"
                    required
                  />
                </div>

                <div className={style.fieldGroup}>
                  <label htmlFor="state">State *</label>
                  <select
                    id="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select State</option>
                    {activeState.map((st) => (
                      <option key={st.id} value={st.name}>
                        {decodeHtml(st.name)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={style.fieldGroup}>
                  <label htmlFor="country">Country *</label>
                  <input
                    type="text"
                    id="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Country"
                    required
                  />
                </div>

                <div className={style.fieldGroup}>
                  <label htmlFor="pin_code">PIN Code *</label>
                  <input
                    type="number"
                    id="pin_code"
                    value={formData.pin_code}
                    onChange={handleInputChange}
                    placeholder="Pin code"
                    required
                  />
                </div>
              </div>

              {formError && (
                <div className={style.errorBanner}>
                  {formError}
                </div>
              )}

              <div className={style.formActionsRow}>
                <button type="submit" className={style.saveSubmitBtn}>
                  {isAdd ? "Save Address" : "Update Address"}
                </button>
                <button
                  type="button"
                  className={style.cancelFormBtn}
                  onClick={() => {
                    setFormActive(false)
                    setFormError("")
                    setFormData({
                      name: "",
                      phone_number: "",
                      line_1: "",
                      line_2: "",
                      city: "",
                      district: "",
                      state: "",
                      country: "",
                      pin_code: "",
                    })
                    setIsAdd(true)
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Page>
  )
}

export default AddressPage
